import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CHIP,
  COLS,
  COL_H,
  COL_Y,
  CYCLE,
  Frame,
  HOLD,
  ORDER_ANSWER,
  ORDER_CARD,
  PEOPLE,
  STORY_END,
  TICKET_ANSWERS,
  TICKET_H,
  TICKET_TOP,
  VIEW,
  arrivalFade,
  chipFlyPos,
  computeFrame,
} from "./checkout-questions-timeline";

/* Storyboard das perguntas do checkout. Três colunas — painel, checkout e
   aba Participantes — e uma comparação no meio:

   1. o produtor escreve a pergunta e escolhe o escopo "Todo pedido";
   2. no checkout ela aparece UMA vez, no bloco do pedido, e o comprador
      responde uma vez só;
   3. na aba Participantes a mesma resposta se repete nas três linhas;
   4. o produtor troca o escopo para "Por ingresso" e salva de novo;
   5. a pergunta desce para dentro de cada ingresso — três respostas — e a
      tabela passa a mostrar três valores diferentes.

   O que a UI não conta: é o escopo, e só ele, que decide quantas respostas
   você recebe. Os quatro campos nativos (nome, e-mail, CPF e telefone) já são
   por ingresso nos dois casos — por isso aparecem nos cartões o tempo todo. */

const CHIP_LABEL = "Tamanho";
const NATIVE_MAIL = ["ana@email.com", "bruno@email.com", "carla@email.com"];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (ev: MediaQueryListEvent) => setReduced(ev.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

/** Cartão selecionável: título em cima, explicação curta embaixo. */
function PickCard({
  x,
  y,
  w,
  h,
  title,
  sub,
  on,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  sub: string;
  on: boolean;
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={7}
        fill={on ? "hsl(var(--primary))" : "hsl(var(--background))"}
        fillOpacity={on ? 0.08 : 1}
        stroke={on ? "hsl(var(--primary))" : "hsl(var(--border))"}
        strokeWidth={on ? 1.5 : 1}
        style={{ transition: "stroke 0.3s ease, fill-opacity 0.3s ease" }}
      />
      <text
        x={x + w / 2}
        y={y + h * 0.42}
        textAnchor="middle"
        className="text-[7.5px] font-bold"
        fill={on ? "hsl(var(--primary))" : "hsl(var(--foreground))"}
        style={{ transition: "fill 0.3s ease" }}
      >
        {title}
      </text>
      <text x={x + w / 2} y={y + h * 0.78} textAnchor="middle" className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
        {sub}
      </text>
    </g>
  );
}

/** A pergunta em voo, saindo do bloco do pedido para dentro de um ingresso. */
function QuestionChip({ x, y, opacity = 1 }: { x: number; y: number; opacity?: number }) {
  const { t } = useTranslation("producerCheckoutQuestions");
  return (
    <g transform={`translate(${x} ${y})`} opacity={opacity}>
      <rect
        x={-CHIP.w / 2}
        y={-CHIP.h / 2}
        width={CHIP.w}
        height={CHIP.h}
        rx={CHIP.h / 2}
        fill="hsl(var(--background))"
        stroke="hsl(var(--primary))"
      />
      <text x={0} y={2.5} textAnchor="middle" className="text-[6.5px] font-semibold" fill="hsl(var(--primary))">
        {t("flowDemo.chipLabel")}
      </text>
    </g>
  );
}

export function CheckoutQuestionsFlowDemo() {
  const { t } = useTranslation("producerCheckoutQuestions");
  const reduced = usePrefersReducedMotion();
  const [frame, setFrame] = useState<Frame>(() => computeFrame(0));

  useEffect(() => {
    if (reduced) {
      setFrame(computeFrame(STORY_END + HOLD / 2));
      return;
    }
    let raf = 0;
    let start: number | null = null;
    const loop = (ts: number) => {
      if (start === null) start = ts;
      setFrame(computeFrame((ts - start) % CYCLE));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const {
    cursor,
    press,
    clickPulse,
    showCursor,
    typed,
    orderPicked,
    attendeePicked,
    savedPulse,
    orderCardIn,
    orderAnswerIn,
    ticketsIn,
    chipFly,
    rowIn,
    answerIn,
    tableIn,
    cellIn,
    cellSwap,
    noteMode,
    hintIn,
    focus,
    stage,
  } = frame;

  const cursorScale = 1 - press * 0.2;
  const colOpacity = (i: number) => (reduced ? 1 : i === focus ? 1 : 0.42);
  const scopeChosen = orderPicked || attendeePicked;

  return (
    <div className="w-full rounded-3xl border border-border/40 bg-secondary/20 p-3 sm:p-4 overflow-hidden">
      <svg viewBox={`0 0 ${VIEW.w} ${VIEW.h}`} className="w-full">
        <g opacity={reduced ? 1 : stage}>
          {/* ── Colunas ── */}
          {COLS.map((c, i) => (
            <g key={c.title} style={{ opacity: colOpacity(i), transition: "opacity 0.45s ease" }}>
              <rect
                x={c.x}
                y={COL_Y}
                width={c.w}
                height={COL_H}
                rx={12}
                fill="hsl(var(--background))"
                stroke="hsl(var(--border))"
                strokeOpacity={i === focus ? 1 : 0.5}
                strokeWidth={1.5}
              />
              <text x={c.x + 12} y={31} className="text-[9px] font-bold" fill="hsl(var(--foreground))">
                {c.title === "Você cria a pergunta" ? t("flowDemo.cols.producerPanel.title") : c.title === "O comprador responde" ? t("flowDemo.cols.checkout.title") : c.title === "Você lê a resposta" ? t("flowDemo.cols.attendeesTab.title") : c.title}
              </text>
              <text x={c.x + 12} y={41} className="text-[7px]" fill="hsl(var(--muted-foreground))">
                {c.sub === "no formulário do evento" ? t("flowDemo.cols.producerPanel.sub") : c.sub === "no checkout, 3 ingressos" ? t("flowDemo.cols.checkout.sub") : c.sub === "na aba Participantes" ? t("flowDemo.cols.attendeesTab.sub") : c.sub}
              </text>
              <line x1={c.x + 12} y1={48} x2={c.x + c.w - 12} y2={48} stroke="hsl(var(--border))" strokeOpacity={0.6} />
            </g>
          ))}

          {/* setas entre as colunas */}
          {[176, 342].map((x, i) => (
            <path
              key={x}
              d={`M${x - 2} 121 l4 4 l-4 4`}
              fill="none"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1.3}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: focus > i ? 0.85 : 0.25, transition: "opacity 0.4s ease" }}
            />
          ))}

          {/* ── Coluna 1: o formulário do produtor ── */}
          <g style={{ opacity: colOpacity(0), transition: "opacity 0.45s ease" }}>
            <text x={26} y={60} className="text-[6.5px] font-semibold" fill="hsl(var(--muted-foreground))">
              {t("flowDemo.producerPanel.question")}
            </text>
            <rect
              x={26}
              y={64}
              width={134}
              height={18}
              rx={6}
              fill="hsl(var(--background))"
              stroke={typed ? "hsl(var(--primary))" : "hsl(var(--border))"}
              style={{ transition: "stroke 0.3s ease" }}
            />
            <text
              x={33}
              y={76}
              className="text-[7px]"
              fill={typed ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))"}
            >
              {typed || t("flowDemo.producerPanel.questionExample")}
            </text>

            <text x={26} y={90} className="text-[6.5px] font-semibold" fill="hsl(var(--muted-foreground))">
              {t("flowDemo.producerPanel.scope")}
            </text>
            <PickCard x={26} y={94} w={64} h={28} title={t("flowDemo.producerPanel.scopeOrder")} sub={t("flowDemo.producerPanel.scopeOrderSub")} on={orderPicked} />
            <PickCard x={94} y={94} w={66} h={28} title={t("flowDemo.producerPanel.scopeTicket")} sub={t("flowDemo.producerPanel.scopeTicketSub")} on={attendeePicked} />

            <text x={26} y={136} className="text-[6.5px] font-semibold" fill="hsl(var(--muted-foreground))">
              {t("flowDemo.producerPanel.tiers")}
            </text>
            <rect
              x={26}
              y={140}
              width={134}
              height={16}
              rx={8}
              fill="hsl(var(--secondary))"
              fillOpacity={0.6}
              stroke="hsl(var(--border))"
            />
            <text x={93} y={151} textAnchor="middle" className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
              {t("flowDemo.producerPanel.tiersEmpty")}
            </text>

            <rect
              x={26}
              y={166}
              width={134}
              height={20}
              rx={8}
              fill="hsl(var(--primary))"
              opacity={typed && scopeChosen ? 1 : 0.35}
              style={{ transition: "opacity 0.4s ease" }}
            />
            <text x={93} y={179.5} textAnchor="middle" className="text-[7.5px] font-bold" fill="hsl(var(--primary-foreground))">
              {savedPulse > 0.5 ? t("flowDemo.producerPanel.saveBtnSaved") : t("flowDemo.producerPanel.saveBtn")}
            </text>

            {/* como a pergunta fica listada depois de salva. Sem transition:
                a opacidade já vem animada quadro a quadro, e a transição do CSS
                só somaria 0.3s de atraso em cima do valor calculado. */}
            <g style={{ opacity: savedPulse }}>
              <rect
                x={26}
                y={194}
                width={134}
                height={32}
                rx={7}
                fill="hsl(var(--secondary))"
                fillOpacity={0.7}
                stroke="hsl(var(--border))"
                strokeDasharray="3 3"
              />
              <text x={34} y={206} className="text-[6.5px] font-semibold" fill="hsl(var(--foreground))">
                {t("flowDemo.producerPanel.savedQuestionName")}
              </text>
              <text x={34} y={218} className="text-[6.5px] font-semibold" fill="hsl(var(--primary))">
                {attendeePicked ? t("flowDemo.producerPanel.savedQuestionScopeTicket") : t("flowDemo.producerPanel.savedQuestionScopeOrder")}
              </text>
              <text x={152} y={218} textAnchor="end" className="text-[6px]" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.producerPanel.savedQuestionType")}
              </text>
            </g>
          </g>

          {/* ── Coluna 2: o checkout ── */}
          <g style={{ opacity: colOpacity(1), transition: "opacity 0.45s ease" }}>
            {/* bloco do pedido: existe só enquanto o escopo é o pedido */}
            <g style={{ opacity: orderCardIn }}>
              <rect
                x={ORDER_CARD.x}
                y={ORDER_CARD.y}
                width={ORDER_CARD.w}
                height={ORDER_CARD.h}
                rx={8}
                fill="hsl(var(--background))"
                stroke="hsl(var(--primary))"
                strokeOpacity={0.55}
              />
              <text x={199} y={66} className="text-[6px] font-bold" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.checkout.orderInfo")}
              </text>
              <text x={199} y={76.5} className="text-[6.5px]" fill="hsl(var(--foreground))">
                {t("flowDemo.checkout.questionName")}
              </text>
              <rect
                x={199}
                y={80}
                width={120}
                height={15}
                rx={5}
                fill="hsl(var(--background))"
                stroke={orderAnswerIn > 0.5 ? "hsl(var(--primary))" : "hsl(var(--border))"}
                style={{ transition: "stroke 0.3s ease" }}
              />
              <text
                x={206}
                y={90.5}
                className="text-[7px] font-bold"
                fill="hsl(var(--primary))"
                opacity={orderAnswerIn}
              >
                {ORDER_ANSWER}
              </text>
              <text x={312} y={90.5} textAnchor="end" className="text-[5.8px]" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.checkout.oneAnswer")}
              </text>
            </g>

            {/* os três ingressos do carrinho */}
            <g style={{ opacity: ticketsIn }}>
              {TICKET_TOP.map((top, i) => {
                // as duas linhas de cima sobem para abrir espaço quando a
                // pergunta desce para dentro do cartão
                const shift = 6 * rowIn[i];
                return (
                  <g key={top}>
                    <rect
                      x={192}
                      y={top}
                      width={134}
                      height={TICKET_H}
                      rx={8}
                      fill="hsl(var(--background))"
                      stroke="hsl(var(--border))"
                    />
                    <rect x={192} y={top} width={3} height={TICKET_H} rx={1.5} fill="hsl(var(--primary))" opacity={0.5} />
                    <text x={200} y={top + 17 - shift} className="text-[7px] font-bold" fill="hsl(var(--foreground))">
                      {t("flowDemo.checkout.ticketLabel")}{i + 1}
                      <tspan className="text-[6.5px] font-normal" fill="hsl(var(--muted-foreground))">
                        {t("flowDemo.checkout.ticketTier")}
                      </tspan>
                    </text>
                    <text x={200} y={top + 26 - shift} className="text-[6px]" fill="hsl(var(--muted-foreground))">
                      {PEOPLE[i]} · {NATIVE_MAIL[i]}
                    </text>

                    {/* a pergunta, quando o escopo é o ingresso */}
                    <g style={{ opacity: rowIn[i] }}>
                      <rect
                        x={199}
                        y={top + 24}
                        width={120}
                        height={13}
                        rx={5}
                        fill="hsl(var(--primary))"
                        fillOpacity={0.07}
                        stroke="hsl(var(--primary))"
                        strokeOpacity={0.45}
                      />
                      <text x={204} y={top + 32.7} className="text-[5.8px]" fill="hsl(var(--muted-foreground))">
                        {t("flowDemo.checkout.questionName")}
                      </text>
                      <text
                        x={314}
                        y={top + 33}
                        textAnchor="end"
                        className="text-[7px] font-bold"
                        fill="hsl(var(--primary))"
                        opacity={answerIn[i]}
                      >
                        {TICKET_ANSWERS[i]}
                      </text>
                    </g>
                  </g>
                );
              })}
            </g>
          </g>

          {/* ── Coluna 3: a aba Participantes ── */}
          <g style={{ opacity: colOpacity(2), transition: "opacity 0.45s ease" }}>
            <g style={{ opacity: tableIn }}>
              <text x={358} y={66} className="text-[6px] font-bold" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.attendeesTab.attendee")}
              </text>
              <text x={494} y={66} textAnchor="end" className="text-[6px] font-bold" fill="hsl(var(--primary))">
                {t("flowDemo.attendeesTab.columnQuestion")}
              </text>
              <line x1={358} y1={70} x2={494} y2={70} stroke="hsl(var(--border))" />

              {PEOPLE.map((name, i) => {
                const top = 76 + i * 27;
                return (
                  <g key={name}>
                    <rect
                      x={358}
                      y={top}
                      width={136}
                      height={22}
                      rx={6}
                      fill="hsl(var(--secondary))"
                      fillOpacity={0.35}
                    />
                    <text x={365} y={top + 14} className="text-[6.5px]" fill="hsl(var(--foreground))">
                      {name}
                    </text>
                    {/* a resposta do pedido sai e a do ingresso entra no mesmo lugar */}
                    <text
                      x={487}
                      y={top + 14.5}
                      textAnchor="end"
                      className="text-[8px] font-bold"
                      fill="hsl(var(--foreground))"
                      opacity={cellIn[i] * (1 - cellSwap[i])}
                    >
                      {ORDER_ANSWER}
                    </text>
                    <text
                      x={487}
                      y={top + 14.5}
                      textAnchor="end"
                      className="text-[8px] font-bold"
                      fill="hsl(var(--primary))"
                      opacity={cellSwap[i]}
                    >
                      {TICKET_ANSWERS[i]}
                    </text>
                  </g>
                );
              })}

              {/* o que aquela coluna está dizendo, em uma frase */}
              <rect x={358} y={162} width={136} height={20} rx={7} fill="hsl(var(--primary))" opacity={0.09} />
              <text
                x={426}
                y={175}
                textAnchor="middle"
                className="text-[6.5px] font-semibold"
                fill="hsl(var(--primary))"
                opacity={1 - noteMode}
              >
                {t("flowDemo.attendeesTab.noteSameAnswer")}
              </text>
              <text
                x={426}
                y={175}
                textAnchor="middle"
                className="text-[6.5px] font-semibold"
                fill="hsl(var(--primary))"
                opacity={noteMode}
              >
                {t("flowDemo.attendeesTab.noteOnePerAttendee")}
              </text>
            </g>

            <g style={{ opacity: hintIn }}>
              <rect
                x={358}
                y={194}
                width={136}
                height={30}
                rx={7}
                fill="none"
                stroke="hsl(var(--border))"
                strokeDasharray="3 3"
              />
              <text x={426} y={206} textAnchor="middle" className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.attendeesTab.hintOutOfTier")}
              </text>
              <text x={426} y={216} textAnchor="middle" className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
                {t("flowDemo.attendeesTab.hintCsv")}
              </text>
            </g>
          </g>

          {/* peças em voo — atravessam cartões, então vêm por cima */}
          {chipFly.map((t, i) => {
            if (t <= 0.001 || t >= 0.999) return null;
            const p = chipFlyPos(i, t);
            return <QuestionChip key={i} x={p.x} y={p.y} opacity={arrivalFade(t)} />;
          })}

          {/* ripple do clique */}
          {!reduced && clickPulse > 0 && (
            <circle
              cx={cursor.x}
              cy={cursor.y}
              r={4 + clickPulse * 12}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              opacity={0.55 * (1 - clickPulse)}
            />
          )}

          {/* cursor */}
          {!reduced && showCursor && (
            <g transform={`translate(${cursor.x} ${cursor.y}) scale(${cursorScale})`}>
              <path
                d="M0,0 L0,20 L5.3,15 L9,22.4 L11.8,21.1 L8.1,13.9 L14,13.6 Z"
                className="fill-background"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.4}
                strokeLinejoin="round"
              />
            </g>
          )}
        </g>
      </svg>
    </div>
  );
}
